import { useQuery } from '@tanstack/react-query';
import { request, gql } from 'graphql-request';

import { QueryKeys } from 'src/constants/queryKeys';

import { Pool } from '@app/types/pool';
import { OLD_WEETH_TOKEN } from '@app/constants/tokens';
import { ApplicationConfig } from '@app/config/types';
import { useConfig } from '@app/config';
import uniqBy from 'lodash/uniqBy';

type QueryParams = {
  tokens: string;
  orderBy: 'created';
  orderDirection: 'desc';
  address: string | undefined;
  search: string;
  config?: ApplicationConfig | undefined;
};

function getQuery(queryParams: QueryParams) {
  if (queryParams.tokens) {
    return gql`
      query {
        pools(
          where: {
            and: [
              { token0_in: [${queryParams.tokens}] },
              { token1_in: [${queryParams.tokens}] },
              {
                or: [
                  {
                    token0_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token1_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  }
                  {
                    token1_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_contains_nocase: "${queryParams.search}"
                  }
                  {
                    token1_contains_nocase: "${queryParams.search}"
                  },
                ] 
              }
            ]
          }
        )
         {
          id
          token0 {
            id
            totalSupply
            symbol
            name
            decimals
            txCount
          }
          token1 {
            id
            totalSupply
            symbol
            name
            decimals
            txCount
          }
          id
          liquidity
          tick
          fee
          totalValueLockedUSD
          volumeUSD
        }
      }
    `;
  }

  return gql`
      query {
        pools(
          where: {
            and: [
              {
                or: [
                  {
                    token0_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token1_: {
                      name_contains_nocase: "${queryParams.search}"
                    }
                  }
                  {
                    token1_: {
                      symbol_contains_nocase: "${queryParams.search}"
                    }
                  },
                  {
                    token0_contains_nocase: "${queryParams.search}"
                  }
                  {
                    token1_contains_nocase: "${queryParams.search}"
                  },
                ] 
              }
            ]
          }
        )
         {
          id
          token0 {
            id
            totalSupply
            symbol
            name
            decimals
            txCount
          }
          token1 {
            id
            totalSupply
            symbol
            name
            decimals
            txCount
          }
          id
          liquidity
          tick
          fee
          totalValueLockedUSD
          volumeUSD
        }
      }
    `;
}

const fetcher = async (
  queryParams: QueryParams,
  config: ApplicationConfig | undefined
) => {
  const query = getQuery(queryParams);

  const res = await request<{ pools: Pool[] }>(
    `${config?.URLS.subgraphUrlV3}`,
    query,
    queryParams
  );

  const pools = res?.pools.map(item => {
    const isOldWeETHToken0 = item.token0.id.toLowerCase() === OLD_WEETH_TOKEN;
    const isOldWeETHToken1 = item.token1.id.toLowerCase() === OLD_WEETH_TOKEN;

    return {
      ...item,
      token0: isOldWeETHToken0
        ? {
            ...item.token0,
            symbol: '(OLD) weETH'
          }
        : item.token0,
      token1: isOldWeETHToken1
        ? {
            ...item.token1,
            symbol: '(OLD) weETH'
          }
        : item.token1,
      isMain: true
    };
  });

  const result: Pool[] = [];

  config?.CONTENT.poolsOrder.forEach((poolId: string) => {
    const pool = pools?.find(p => p.id.toLowerCase() === poolId.toLowerCase());

    if (pool) {
      result.push(pool);
    }
  });

  const lowered = config?.CONTENT.poolsOrder.map((p: string) =>
    p.toLowerCase()
  );
  const unordered = pools?.filter(p => !lowered?.includes(p.id.toLowerCase()));

  return uniqBy([...result, ...unordered], item => item.id);
};

export function useVerifiedPoolsListV3(params: QueryParams) {
  const config = useConfig();

  return useQuery<Pool[] | undefined>(
    [QueryKeys.POOLS, { params }],
    () => {
      return fetcher(params, params.config ?? config);
    },
    {
      staleTime: 60000,
      refetchOnWindowFocus: false,
      enabled: Boolean(params.config || config)
    }
  );
}
